/**
 * AFSNITTET I ADRESSEN ([D-554], Charlie 05-11-2026; 12-conventions §103).
 *
 * Mens man scroller, står det afsnit man læser, SIDST i adressen:
 *
 *   /#/marketing/blended/geography?from=…&map_level=region#omsaetning-pr-geografi
 *
 * Deles linket, åbner siden øverst og scroller derefter blødt ned til
 * afsnittet. Det gælder alle sider på én gang, fordi ankrene allerede findes:
 * hver SectionTitle har id `sec-<slug>` ([D-198]), og fragmentet er slug'en
 * uden prefix. Ordbogens begreber (`ord-<term>`) tæller med under deres eget
 * id. Øverst på siden (før første afsnit) har adressen intet fragment.
 *
 * TO FÆLDER, som mekanikken er bygget om:
 *
 * 1. Fragmentet skrives med `history.replaceState`, ikke `router.replace`. En
 *    router-navigation pr. afsnitsskift ville køre alle guards og vække sidernes
 *    `watch(route.query)` midt i en scroll. Routerens egen `route.hash` er derfor
 *    kun sand ved indgangen; sandheden bor her (`current`).
 * 2. Sidernes query-skrivninger (`router.replace({ query })`) taber fragmentet,
 *    fordi vue-router sætter hash = '' når den ikke er angivet. Efter hver
 *    navigation på samme side lægges fragmentet derfor på igen, så det altid
 *    står sidst.
 *
 * Ved indgangen venter mekanikken, til afsnittet findes, sidens første data er
 * hentet og afsnittets plads har ligget stille et øjeblik; først da scrolles der.
 * Flytter afsnittet sig bagefter (sene data over det), rettes der op, indtil
 * brugeren selv tager over (hjul, berøring, tast, klik).
 */

import type { Router } from 'vue-router';

import { ANCHOR_PREFIX } from './anchor';
import { isPageLoading } from './saving';

/** Ordbogens ankre (GlossaryDocView.slug), som står i adressen med prefix. */
const GLOSSARY_PREFIX = 'ord-';
/** Afsnit i sidens indhold. Drawere og dialoger står uden for <main> eller er faste. */
const SECTION_SELECTOR = `main [id^="${ANCHOR_PREFIX}"], main [id^="${GLOSSARY_PREFIX}"]`;
/** Så længe ventes der på, at afsnittet tegnes (kolde dashboards i prod er langsomme, [D-196]). */
const FIND_TIMEOUT_MS = 20_000;
/** Afsnittets plads skal ligge stille så længe, før der scrolles. */
const SETTLE_MS = 300;
/** Højst så længe ventes der på ro, efter at afsnittet er fundet. */
const SETTLE_TIMEOUT_MS = 8_000;
/** Så længe holdes afsnittet på plads efter scrollet, hvis indhold over det vokser. */
const PIN_MS = 12_000;
const TICK_MS = 150;
/** Fragmentet skrives først, når scrollen har stået stille så længe (Safari begrænser replaceState). */
const WRITE_DEBOUNCE_MS = 120;

/** Afsnittets slug, som det står (eller skal stå) sidst i adressen. */
let current: string | null = null;
/** Indgangen der er i gang; en ny indgang eller brugerens egen scroll afløser den. */
let arrival: { cancelled: boolean } | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;

/** '#/side?x=1#afsnit' → base '#/side?x=1' og slug 'afsnit'. */
function splitLocationHash(): { base: string; slug: string | null } {
  const h = window.location.hash;
  const i = h.indexOf('#', 1);
  if (i < 0) return { base: h, slug: null };
  return { base: h.slice(0, i), slug: decodeURIComponent(h.slice(i + 1)) || null };
}

/** Skriv slug'en sidst i adressen uden en router-navigation. */
function writeSlug(slug: string | null): void {
  const { base, slug: shown } = splitLocationHash();
  if (!base.startsWith('#/') || shown === slug) return;
  const fullPath = base.slice(1) + (slug ? `#${slug}` : '');
  // vue-router bygger næste push ud fra state.current, så den skal kende fragmentet,
  // ellers skrives siden uden afsnit tilbage i historikken, når man går videre.
  const state = { ...(window.history.state ?? {}), current: fullPath };
  try {
    window.history.replaceState(state, '', `${window.location.pathname}${window.location.search}#${fullPath}`);
  } catch {
    /* for mange skrivninger (Safari): næste scroll prøver igen */
  }
}

/** Element-id → fragment: afsnittenes `sec-` står ikke i adressen. */
function slugOf(id: string): string {
  return id.startsWith(ANCHOR_PREFIX) ? id.slice(ANCHOR_PREFIX.length) : id;
}

/** Fragment → element (afsnit først, ellers et ordbogsbegreb). */
function elementFor(slug: string): HTMLElement | null {
  const section = document.getElementById(`${ANCHOR_PREFIX}${slug}`);
  if (section) return section;
  return slug.startsWith(GLOSSARY_PREFIX) ? document.getElementById(slug) : null;
}

function sections(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(SECTION_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0 && !el.closest('.fixed, [role="dialog"]'),
  );
}

function scrollMargin(el: HTMLElement): number {
  return parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
}

/** Så langt under ankerets landingssted må en overskrift stå og stadig regnes for nået. */
const REACHED_SLACK_PX = 64;

/**
 * Afsnittet man læser = det afsnit, et delt link ville vise: det, hvis
 * landingsposition (overskriften under headeren, højst sidens bund) senest er
 * nået. Ved bunden kan flere afsnit have samme landingsposition; så vinder det
 * øverste, for linket lander det samme sted. Øverst på siden: intet afsnit.
 */
function readingSection(): HTMLElement | null {
  const y = window.scrollY;
  if (y < 1) return null;
  let best: HTMLElement | null = null;
  let bestTarget = -Infinity;
  for (const el of sections()) {
    const target = targetTop(el);
    if (target <= y + REACHED_SLACK_PX && target > bestTarget) {
      best = el;
      bestTarget = target;
    }
  }
  return best;
}

function onScroll(): void {
  if (arrival) return;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    writeTimer = null;
    if (arrival) return;
    const el = readingSection();
    current = el ? slugOf(el.id) : null;
    writeSlug(current);
  }, WRITE_DEBOUNCE_MS);
}

/** Brugeren tager selv over: indgangens scroll og oprydning stopper. */
function onUserInput(): void {
  if (arrival) {
    arrival.cancelled = true;
    arrival = null;
  }
}

function targetTop(el: HTMLElement): number {
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const top = el.getBoundingClientRect().top + window.scrollY - scrollMargin(el);
  return Math.max(0, Math.min(max, Math.round(top)));
}

/**
 * Scroll blødt til afsnittet, når siden er klar til det, og hold det på plads.
 * `idOrSlug` er afsnittets element-id eller fragmentet fra adressen. Findes
 * afsnittet aldrig, står man blot på siden.
 */
export function goToSection(idOrSlug: string): void {
  const slug = slugOf(idOrSlug);
  if (arrival) arrival.cancelled = true;
  const me = { cancelled: false };
  arrival = me;
  current = slug;
  writeSlug(slug);
  const started = Date.now();
  let foundAt = 0;
  let lastTop = NaN;
  let stillSince = 0;

  const done = (): void => {
    if (arrival === me) arrival = null;
  };

  const pin = (el: HTMLElement, until: number): void => {
    let lastY = window.scrollY;
    let movedAt = Date.now();
    const check = (): void => {
      if (me.cancelled) return;
      if (Date.now() > until || !el.isConnected) return done();
      const y = window.scrollY;
      if (y !== lastY) {
        lastY = y;
        movedAt = Date.now();
      } else if (Date.now() - movedAt > TICK_MS && Math.abs(targetTop(el) - y) > 4) {
        // Indhold over afsnittet er vokset (sene data): ret op igen.
        window.scrollTo({ top: targetTop(el), behavior: 'smooth' });
        movedAt = Date.now();
      }
      window.setTimeout(check, TICK_MS);
    };
    window.setTimeout(check, TICK_MS);
  };

  const tick = (): void => {
    if (me.cancelled) return;
    const now = Date.now();
    const el = elementFor(slug);
    if (!el) {
      if (now - started < FIND_TIMEOUT_MS) window.setTimeout(tick, TICK_MS);
      else done();
      return;
    }
    if (!foundAt) foundAt = now;
    const top = el.getBoundingClientRect().top + window.scrollY;
    if (top !== lastTop) {
      lastTop = top;
      stillSince = now;
    }
    const settled = !isPageLoading() && now - stillSince >= SETTLE_MS;
    if (!settled && now - foundAt < SETTLE_TIMEOUT_MS) {
      window.setTimeout(tick, TICK_MS);
      return;
    }
    window.scrollTo({ top: targetTop(el), behavior: 'smooth' });
    el.classList.add('anchor-flash');
    window.setTimeout(() => el.classList.remove('anchor-flash'), 1600);
    pin(el, Date.now() + PIN_MS);
  };
  window.setTimeout(tick, TICK_MS);
}

/** Kaldes én gang i router.ts lige efter createRouter. */
export function installSectionHash(router: Router): void {
  window.addEventListener('scroll', onScroll, { passive: true });
  for (const type of ['wheel', 'touchstart', 'keydown', 'mousedown'] as const) {
    window.addEventListener(type, onUserInput, { passive: true, capture: true });
  }

  router.afterEach((to, from, failure) => {
    if (failure) return;
    const slug = to.hash ? decodeURIComponent(to.hash.slice(1)) || null : null;
    const samePage = to.path === from.path && from.matched.length > 0;
    if (samePage) {
      // En ny hash i en navigation er et ønske om at gå til afsnittet; ellers er det
      // sidens egen query-skrivning, som har tabt (eller forældet) fragmentet.
      if (slug && slug !== current && to.hash !== from.hash) goToSection(slug);
      else writeSlug(current);
      return;
    }
    if (arrival) arrival.cancelled = true;
    arrival = null;
    current = null;
    if (slug) goToSection(slug);
  });
}
